import * as React from 'react';
import { type ComponentProps } from 'react';
import { useConfigProperty } from '@leagueplatform/core';
import { Box } from '@leagueplatform/genesis-core';

const DEFAULT_STYLES = {
  height: '140px',
  width: '20024px',
  backgroundColor: '$surfaceScreenHeaderBackground',
};

type PageHeaderBackgroundProps = ComponentProps<typeof Box> & {
  curved?: boolean;
  height?: string;
  pageHeaderBackgroundHeight?: string;
};

export const PageHeaderBackground = ({
  height = DEFAULT_STYLES.height,
  className,
  css,
  curved,
  ...rest
}: PageHeaderBackgroundProps) => {
  const pageHeaderRef = React.useRef<HTMLDivElement>(null);
  const pageHeaderConfig = useConfigProperty('core.ui.components.pageHeader');
  const composedClassName = React.useMemo(
    () => ['cc-page-header-background', className].join(' '),
    [className],
  );
  const isCurved = curved || pageHeaderConfig?.shape === 'swoop';

  const curvedStyles = React.useMemo(
    () => ({
      borderRadius: DEFAULT_STYLES.width,
      height: `${DEFAULT_STYLES.width}`,
      insetInlineStart: `calc(50% - (${DEFAULT_STYLES.width} / 2))`,
      bottom: 0,
      width: `${DEFAULT_STYLES.width}`,
    }),
    [],
  );

  const lineStyles = React.useMemo(
    () => ({
      top: 0,
      bottom: 0,
      left: `calc(-50vw + 50%)`,
      right: `calc(-50vw + 50%)`,
    }),
    [],
  );

  const beforeStyles = React.useMemo(() => {
    if (isCurved) {
      return curvedStyles;
    }
    return lineStyles;
  }, [curvedStyles, lineStyles, isCurved]);

  return (
    <Box
      ref={pageHeaderRef}
      css={{
        backgroundColor: 'transparent',
        width: '100%',
        position: 'relative',
        minHeight: height,
        paddingBlock: '$three',
        '@mobile': {
          paddingBlock: '$oneAndHalf',
        },
        '&:before': {
          backgroundColor: DEFAULT_STYLES.backgroundColor,
          content: '""',
          zIndex: '-1',
          position: 'absolute',
          ...beforeStyles,
        },
        ...css,
      }}
      className={composedClassName}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
};
